import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  User, Mail, Lock, Phone, Globe, ChevronRight, 
  Loader2, Check, ArrowLeft, Eye, EyeOff, Shield,
  CreditCard, Building2, Smartphone, MapPin, Search, ChevronDown, Upload, Calendar
} from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const API = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_CLIENT_ID = 'AYXMwKbKbTQ84KxiAMGz-scb23b6PVpu-DPQjEz98JTdDvvIctq8dN_jjEIUGLM718Q_ANv9FizfFKsJ';

// Ahmad Raza's actual image
const AHMAD_RAZA_IMAGE = "https://customer-assets.emergentagent.com/job_fee64ab8-866e-4873-bfd8-a2b3caadae98/artifacts/i2hvejea_ChatGPT%20Image%20Jan%2026%2C%202026%2C%2008_46_38%20PM.png";

// Country list with codes (expanded)
const COUNTRIES = [
  { code: 'AF', name: 'Afghanistan', dialCode: '+93', flag: '🇦🇫' },
  { code: 'AL', name: 'Albania', dialCode: '+355', flag: '🇦🇱' },
  { code: 'DZ', name: 'Algeria', dialCode: '+213', flag: '🇩🇿' },
  { code: 'AR', name: 'Argentina', dialCode: '+54', flag: '🇦🇷' },
  { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
  { code: 'AT', name: 'Austria', dialCode: '+43', flag: '🇦🇹' },
  { code: 'BH', name: 'Bahrain', dialCode: '+973', flag: '🇧🇭' },
  { code: 'BD', name: 'Bangladesh', dialCode: '+880', flag: '🇧🇩' },
  { code: 'BE', name: 'Belgium', dialCode: '+32', flag: '🇧🇪' },
  { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷' },
  { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
  { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
  { code: 'CO', name: 'Colombia', dialCode: '+57', flag: '🇨🇴' },
  { code: 'HR', name: 'Croatia', dialCode: '+385', flag: '🇭🇷' },
  { code: 'CZ', name: 'Czech Republic', dialCode: '+420', flag: '🇨🇿' },
  { code: 'DK', name: 'Denmark', dialCode: '+45', flag: '🇩🇰' },
  { code: 'EG', name: 'Egypt', dialCode: '+20', flag: '🇪🇬' },
  { code: 'FI', name: 'Finland', dialCode: '+358', flag: '🇫🇮' },
  { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
  { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
  { code: 'GH', name: 'Ghana', dialCode: '+233', flag: '🇬🇭' },
  { code: 'GR', name: 'Greece', dialCode: '+30', flag: '🇬🇷' },
  { code: 'HK', name: 'Hong Kong', dialCode: '+852', flag: '🇭🇰' },
  { code: 'HU', name: 'Hungary', dialCode: '+36', flag: '🇭🇺' },
  { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
  { code: 'ID', name: 'Indonesia', dialCode: '+62', flag: '🇮🇩' },
  { code: 'IR', name: 'Iran', dialCode: '+98', flag: '🇮🇷' },
  { code: 'IQ', name: 'Iraq', dialCode: '+964', flag: '🇮🇶' },
  { code: 'IE', name: 'Ireland', dialCode: '+353', flag: '🇮🇪' },
  { code: 'IL', name: 'Israel', dialCode: '+972', flag: '🇮🇱' },
  { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹' },
  { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
  { code: 'JO', name: 'Jordan', dialCode: '+962', flag: '🇯🇴' },
  { code: 'KE', name: 'Kenya', dialCode: '+254', flag: '🇰🇪' },
  { code: 'KW', name: 'Kuwait', dialCode: '+965', flag: '🇰🇼' },
  { code: 'LB', name: 'Lebanon', dialCode: '+961', flag: '🇱🇧' },
  { code: 'MY', name: 'Malaysia', dialCode: '+60', flag: '🇲🇾' },
  { code: 'MX', name: 'Mexico', dialCode: '+52', flag: '🇲🇽' },
  { code: 'MA', name: 'Morocco', dialCode: '+212', flag: '🇲🇦' },
  { code: 'NL', name: 'Netherlands', dialCode: '+31', flag: '🇳🇱' },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64', flag: '🇳🇿' },
  { code: 'NG', name: 'Nigeria', dialCode: '+234', flag: '🇳🇬' },
  { code: 'NO', name: 'Norway', dialCode: '+47', flag: '🇳🇴' },
  { code: 'OM', name: 'Oman', dialCode: '+968', flag: '🇴🇲' },
  { code: 'PK', name: 'Pakistan', dialCode: '+92', flag: '🇵🇰' },
  { code: 'PH', name: 'Philippines', dialCode: '+63', flag: '🇵🇭' },
  { code: 'PL', name: 'Poland', dialCode: '+48', flag: '🇵🇱' },
  { code: 'PT', name: 'Portugal', dialCode: '+351', flag: '🇵🇹' },
  { code: 'QA', name: 'Qatar', dialCode: '+974', flag: '🇶🇦' },
  { code: 'RO', name: 'Romania', dialCode: '+40', flag: '🇷🇴' },
  { code: 'RU', name: 'Russia', dialCode: '+7', flag: '🇷🇺' },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', flag: '🇸🇦' },
  { code: 'SG', name: 'Singapore', dialCode: '+65', flag: '🇸🇬' },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦' },
  { code: 'KR', name: 'South Korea', dialCode: '+82', flag: '🇰🇷' },
  { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸' },
  { code: 'LK', name: 'Sri Lanka', dialCode: '+94', flag: '🇱🇰' },
  { code: 'SE', name: 'Sweden', dialCode: '+46', flag: '🇸🇪' },
  { code: 'CH', name: 'Switzerland', dialCode: '+41', flag: '🇨🇭' },
  { code: 'TW', name: 'Taiwan', dialCode: '+886', flag: '🇹🇼' },
  { code: 'TH', name: 'Thailand', dialCode: '+66', flag: '🇹🇭' },
  { code: 'TR', name: 'Turkey', dialCode: '+90', flag: '🇹🇷' },
  { code: 'UA', name: 'Ukraine', dialCode: '+380', flag: '🇺🇦' },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971', flag: '🇦🇪' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
  { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
  { code: 'VN', name: 'Vietnam', dialCode: '+84', flag: '🇻🇳' },
  { code: 'OTHER', name: 'Other', dialCode: '', flag: '🌍' },
];

// PayPal Icon Component
const PayPalIcon = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .76-.654h6.96c2.3 0 4.063.584 5.09 1.686.934 1.005 1.28 2.404 1.03 4.153-.32 2.262-1.14 3.973-2.44 5.09-1.29 1.104-3.05 1.666-5.23 1.666H8.766a.771.771 0 0 0-.76.654l-.93 5.022z" fill="#003087"/>
    <path d="M18.973 7.871c-.33 2.334-1.957 2.334-3.537 2.334h-.897l.631-3.998a.39.39 0 0 1 .385-.328h.412c1.075 0 2.09 0 2.614.612.314.366.41.91.392 1.38z" fill="#002F86"/>
    <path d="M19.737 7.576c-.007.047-.014.096-.022.146-.656 3.374-2.899 4.54-5.764 4.54H12.54a.709.709 0 0 0-.7.6l-.88 5.579-.25 1.582a.373.373 0 0 0 .368.432h2.586a.623.623 0 0 0 .615-.526l.025-.132.488-3.09.031-.17a.623.623 0 0 1 .616-.527h.388c2.512 0 4.478-1.02 5.053-3.972.24-1.233.116-2.262-.52-2.985a2.476 2.476 0 0 0-.623-.477z" fill="#009CDE"/>
  </svg>
);

// Payment Method Button Component
const PaymentMethodButton = ({ icon, bgColor, hoverColor, title, subtitle, onClick, badge }) => (
  <button
    onClick={onClick}
    className={`w-full p-4 border-2 border-slate-200 rounded-xl ${hoverColor} transition-colors text-left flex items-center gap-4 group`}
  >
    <div className={`w-12 h-12 ${bgColor} rounded-xl flex items-center justify-center`}>
      {icon}
    </div>
    <div className="flex-1">
      <p className="font-semibold text-slate-900">{title}</p>
      <p className="text-sm text-slate-500">{subtitle}</p>
    </div>
    {badge ? badge : <ChevronRight className="w-5 h-5 text-slate-400" />}
  </button>
);

// Easypaisa Accordion Component
const EasypaisaAccordion = ({ selectedPackage, registeredUser, onSuccess }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [paymentDate, setPaymentDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReceiptFile(file);
    }
  };

  const handleSubmit = async () => {
    if (!receiptFile || !paymentDate) {
      toast.error('Please upload receipt and select payment date');
      return;
    }
    
    setUploading(true);
    try {
      // First initiate the manual payment
      const initRes = await axios.post(`${API}/api/payments/manual/initiate`, {
        package_id: selectedPackage?.id || 'complete',
        payment_method: 'easypaisa',
        user_email: registeredUser?.email,
        user_name: registeredUser?.name
      });
      
      const transactionId = initRes.data.transaction_id;
      
      // Upload receipt
      const formData = new FormData();
      formData.append('receipt', receiptFile);
      formData.append('payment_date', paymentDate);
      
      await axios.post(`${API}/api/payments/manual/upload-receipt/${transactionId}`, formData);
      
      toast.success('Receipt uploaded! Your account will be activated after verification.');
      onSuccess();
    } catch (err) {
      toast.error('Failed to submit payment. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border-2 border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 hover:bg-green-50 transition-colors text-left flex items-center gap-4"
      >
        <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
          <Smartphone className="w-6 h-6 text-green-600" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-slate-900">Easypaisa</p>
          <p className="text-sm text-slate-500">Mobile wallet transfer</p>
        </div>
        <Badge className="bg-green-100 text-green-700 mr-2">Pakistan</Badge>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="p-4 bg-green-50 border-t border-slate-200">
          <div className="bg-white rounded-xl p-4 mb-4">
            <p className="text-sm font-medium text-slate-700 mb-2">Send payment to:</p>
            <div className="bg-green-100 rounded-lg p-3">
              <p className="text-lg font-bold text-green-800">+92 345 5222 000</p>
              <p className="text-sm text-green-600">Account: Ahmad Raza</p>
            </div>
            <p className="text-xs text-slate-500 mt-2">Amount: £{selectedPackage?.price || '149'}</p>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Payment Date</label>
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Upload Receipt</label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*,.pdf"
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full px-3 py-2 border-2 border-dashed border-slate-300 rounded-lg hover:border-green-500 transition-colors flex items-center justify-center gap-2"
              >
                <Upload className="w-5 h-5 text-slate-400" />
                {receiptFile ? receiptFile.name : 'Click to upload receipt'}
              </button>
            </div>
            
            <Button
              onClick={handleSubmit}
              disabled={uploading || !receiptFile || !paymentDate}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Submit Payment
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Bank Transfer Accordion Component
const BankTransferAccordion = ({ selectedPackage, registeredUser, onSuccess }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [paymentDate, setPaymentDate] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setReceiptFile(file);
    }
  };

  const handleSubmit = async () => {
    if (!receiptFile || !paymentDate) {
      toast.error('Please upload receipt and select payment date');
      return;
    }
    
    setUploading(true);
    try {
      // First initiate the manual payment
      const initRes = await axios.post(`${API}/api/payments/manual/initiate`, {
        package_id: selectedPackage?.id || 'complete',
        payment_method: 'bank_transfer',
        user_email: registeredUser?.email,
        user_name: registeredUser?.name
      });
      
      const transactionId = initRes.data.transaction_id;
      
      // Upload receipt
      const formData = new FormData();
      formData.append('receipt', receiptFile);
      formData.append('payment_date', paymentDate);
      
      await axios.post(`${API}/api/payments/manual/upload-receipt/${transactionId}`, formData);
      
      toast.success('Receipt uploaded! Your account will be activated after verification.');
      onSuccess();
    } catch (err) {
      toast.error('Failed to submit payment. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border-2 border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 hover:bg-purple-50 transition-colors text-left flex items-center gap-4"
      >
        <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
          <Building2 className="w-6 h-6 text-purple-600" />
        </div>
        <div className="flex-1">
          <p className="font-semibold text-slate-900">Bank Transfer</p>
          <p className="text-sm text-slate-500">Direct bank transfer</p>
        </div>
        <Badge className="bg-purple-100 text-purple-700 mr-2">Pakistan</Badge>
        <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <div className="p-4 bg-purple-50 border-t border-slate-200">
          <div className="bg-white rounded-xl p-4 mb-4">
            <p className="text-sm font-medium text-slate-700 mb-2">Bank Details:</p>
            <div className="bg-purple-100 rounded-lg p-3 space-y-1">
              <p className="text-sm"><span className="text-purple-600">Bank:</span> <span className="font-semibold text-purple-800">Allied Bank</span></p>
              <p className="text-sm"><span className="text-purple-600">Account Title:</span> <span className="font-semibold text-purple-800">Ahmad Raza</span></p>
              <p className="text-sm"><span className="text-purple-600">Account No:</span> <span className="font-bold text-purple-800">57650010034179240018</span></p>
            </div>
            <p className="text-xs text-slate-500 mt-2">Amount: £{selectedPackage?.price || '149'}</p>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Payment Date</label>
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Upload Receipt</label>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*,.pdf"
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full px-3 py-2 border-2 border-dashed border-slate-300 rounded-lg hover:border-purple-500 transition-colors flex items-center justify-center gap-2"
              >
                <Upload className="w-5 h-5 text-slate-400" />
                {receiptFile ? receiptFile.name : 'Click to upload receipt'}
              </button>
            </div>
            
            <Button
              onClick={handleSubmit}
              disabled={uploading || !receiptFile || !paymentDate}
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              Submit Payment
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

function RegisterPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  
  // Get selected package from URL
  const selectedPackageId = searchParams.get('package') || 'complete';
  
  // Steps: 1=Details, 2=OTP, 3=Password, 4=Payment
  const [step, setStep] = useState(1);
  
  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Searchable dropdown states
  const [countrySearch, setCountrySearch] = useState('');
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [codeSearch, setCodeSearch] = useState('');
  const [codeDropdownOpen, setCodeDropdownOpen] = useState(false);
  const countryDropdownRef = useRef(null);
  const codeDropdownRef = useRef(null);
  
  // OTP
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  
  // Registration
  const [registering, setRegistering] = useState(false);
  
  // Payment
  const [packages, setPackages] = useState(null);
  const [registeredUser, setRegisteredUser] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // OTP input refs
  const otpRefs = [
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
    React.useRef(null)
  ];

  useEffect(() => {
    fetchPackages();
  }, []);

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const fetchPackages = async () => {
    try {
      const res = await axios.get(`${API}/api/payments/info`);
      setPackages(res.data.packages);
    } catch (err) {
      console.error('Failed to fetch packages:', err);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(e.target)) {
        setCountryDropdownOpen(false);
      }
      if (codeDropdownRef.current && !codeDropdownRef.current.contains(e.target)) {
        setCodeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedPackage = packages?.[selectedPackageId];
  const isPakistan = country === 'PK';
  
  // Get selected country object
  const selectedCountryObj = COUNTRIES.find(c => c.code === country);

  // Filter countries based on search
  const filteredCountries = COUNTRIES.filter(c => 
    c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    c.code.toLowerCase().includes(countrySearch.toLowerCase())
  );

  // Filter countries for code search (by dial code or country name)
  const filteredCodes = COUNTRIES.filter(c => 
    c.dialCode.includes(codeSearch) ||
    c.name.toLowerCase().includes(codeSearch.toLowerCase()) ||
    c.code.toLowerCase().includes(codeSearch.toLowerCase())
  );

  const handleCountrySelect = (selectedCountry) => {
    setCountry(selectedCountry.code);
    setCountryCode(selectedCountry.dialCode);
    setCountrySearch('');
    setCountryDropdownOpen(false);
  };

  const handleCodeSelect = (selectedCountry) => {
    setCountryCode(selectedCountry.dialCode);
    setCodeSearch('');
    setCodeDropdownOpen(false);
  };

  const handleSendOTP = async () => {
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!email.trim() || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    if (!country) {
      toast.error('Please select your country');
      return;
    }

    setOtpSending(true);
    try {
      await axios.post(`${API}/api/auth/send-otp`, {
        email,
        name,
        phone: countryCode + phone,
        country,
        country_code: countryCode
      });
      toast.success('OTP sent to your email!');
      setStep(2);
      setResendTimer(60);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setOtpSending(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length > 1) value = value[0];
    if (!/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      toast.error('Please enter the complete 6-digit OTP');
      return;
    }

    setOtpVerifying(true);
    try {
      await axios.post(`${API}/api/auth/verify-otp`, {
        email,
        otp: otpString
      });
      toast.success('Email verified!');
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid OTP');
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleRegister = async () => {
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setRegistering(true);
    try {
      const res = await axios.post(`${API}/api/auth/register-with-otp`, {
        email,
        name,
        password,
        phone: countryCode + phone,
        country,
        country_code: countryCode,
        otp_verified: true,
        selected_package: selectedPackageId
      });

      // Store the registered user info for payment
      setRegisteredUser({
        token: res.data.access_token,
        user: res.data.user
      });

      toast.success('Account created successfully!');
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setRegistering(false);
    }
  };

  const handlePaymentSuccess = () => {
    if (registeredUser) {
      loginWithToken(registeredUser.token, registeredUser.user);
      navigate('/dashboard');
    }
  };

  const handleSkipPayment = () => {
    if (registeredUser) {
      loginWithToken(registeredUser.token, registeredUser.user);
      navigate('/dashboard');
    }
  };

  const handleStripePayment = async () => {
    setProcessingPayment(true);
    try {
      const res = await axios.post(`${API}/api/payments/stripe/checkout`, {
        package_id: selectedPackageId,
        origin_url: window.location.origin,
        user_email: email,
        user_name: name
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error('Failed to initiate payment. Please try again.');
      setProcessingPayment(false);
    }
  };

  const handlePayPalPayment = () => {
    // Show inline PayPal buttons
    setShowPayPalButtons(true);
  };

  const onPayPalError = (err) => {
    console.error('PayPal error:', err);
    toast.error('Payment failed. Please try again.');
  };

  const onPayPalCancel = () => {
    toast.info('Payment cancelled');
    setShowPayPalButtons(false);
  };

  const [showPayPalButtons, setShowPayPalButtons] = useState(false);

  const createPayPalOrder = (data, actions) => {
    const pkg = packages?.[selectedPackageId];
    return actions.order.create({
      purchase_units: [{
        description: pkg?.name || 'Innovator Founder Visas Package',
        amount: {
          currency_code: 'GBP',
          value: (pkg?.price || 149).toString()
        }
      }]
    });
  };

  const onPayPalApprove = async (data, actions) => {
    setProcessingPayment(true);
    try {
      const details = await actions.order.capture();
      const pkg = packages?.[selectedPackageId];
      
      await axios.post(`${API}/api/payments/paypal/capture`, {
        order_id: data.orderID,
        package_id: selectedPackageId,
        payer_email: details.payer?.email_address || email,
        user_email: email,
        user_name: name,
        amount: pkg?.price || 149
      });

      toast.success('Payment successful! Welcome to Innovator Founder Visas.');
      if (registeredUser) {
        loginWithToken(registeredUser.token, registeredUser.user);
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error('Payment processing failed. Please contact support.');
    } finally {
      setProcessingPayment(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900">innovatorfoundervisas.com</span>
          </Link>

          {/* Progress Steps */}
          <div className="flex items-center gap-2 mb-8">
            {[1, 2, 3, 4].map((s) => (
              <React.Fragment key={s}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  step >= s ? 'bg-red-500 text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  {step > s ? <Check className="w-4 h-4" /> : s}
                </div>
                {s < 4 && <div className={`flex-1 h-1 rounded ${step > s ? 'bg-red-500' : 'bg-slate-200'}`} />}
              </React.Fragment>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {/* Step 1: User Details */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h1 className="text-2xl font-bold text-slate-900 mb-2">Create Your Account</h1>
                <p className="text-slate-600 mb-6">Enter your details to get started</p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Enter your full name"
                        className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
                    <div className="relative" ref={countryDropdownRef}>
                      <button
                        type="button"
                        onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
                        className="w-full pl-10 pr-10 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white text-left"
                      >
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        {selectedCountryObj ? (
                          <span>{selectedCountryObj.flag} {selectedCountryObj.name}</span>
                        ) : (
                          <span className="text-slate-400">Select your country</span>
                        )}
                        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      </button>
                      
                      {countryDropdownOpen && (
                        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 overflow-hidden">
                          <div className="p-2 border-b border-slate-100">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <input
                                type="text"
                                value={countrySearch}
                                onChange={(e) => setCountrySearch(e.target.value)}
                                placeholder="Search country..."
                                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                autoFocus
                              />
                            </div>
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {filteredCountries.length === 0 ? (
                              <div className="px-4 py-3 text-sm text-slate-500">No countries found</div>
                            ) : (
                              filteredCountries.map(c => (
                                <button
                                  key={c.code}
                                  type="button"
                                  onClick={() => handleCountrySelect(c)}
                                  className={`w-full px-4 py-2.5 text-left hover:bg-slate-50 flex items-center gap-3 text-sm ${country === c.code ? 'bg-red-50 text-red-600' : ''}`}
                                >
                                  <span className="text-lg">{c.flag}</span>
                                  <span className="flex-1">{c.name}</span>
                                  <span className="text-slate-400 text-xs">{c.dialCode}</span>
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                    <div className="flex gap-2">
                      <div className="w-28 relative" ref={codeDropdownRef}>
                        <button
                          type="button"
                          onClick={() => setCodeDropdownOpen(!codeDropdownOpen)}
                          className="w-full px-3 py-3 border border-slate-300 rounded-xl bg-white text-center flex items-center justify-center gap-1"
                        >
                          {countryCode || <span className="text-slate-400">+00</span>}
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        </button>
                        
                        {codeDropdownOpen && (
                          <div className="absolute z-50 mt-1 w-64 bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 overflow-hidden left-0">
                            <div className="p-2 border-b border-slate-100">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                  type="text"
                                  value={codeSearch}
                                  onChange={(e) => setCodeSearch(e.target.value)}
                                  placeholder="Search by code or country..."
                                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                  autoFocus
                                />
                              </div>
                            </div>
                            <div className="max-h-48 overflow-y-auto">
                              {filteredCodes.length === 0 ? (
                                <div className="px-4 py-3 text-sm text-slate-500">No codes found</div>
                              ) : (
                                filteredCodes.map(c => (
                                  <button
                                    key={c.code}
                                    type="button"
                                    onClick={() => handleCodeSelect(c)}
                                    className={`w-full px-4 py-2.5 text-left hover:bg-slate-50 flex items-center gap-3 text-sm ${countryCode === c.dialCode ? 'bg-red-50 text-red-600' : ''}`}
                                  >
                                    <span>{c.flag}</span>
                                    <span className="flex-1">{c.name}</span>
                                    <span className="font-medium">{c.dialCode}</span>
                                  </button>
                                ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="tel"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                          placeholder="Phone number"
                          className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleSendOTP}
                  disabled={otpSending}
                  className="w-full mt-6 bg-red-500 hover:bg-red-600 py-6"
                >
                  {otpSending ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-5 h-5 mr-2" />
                  )}
                  Send Verification Code
                </Button>

                <p className="text-center text-sm text-slate-500 mt-4">
                  Already have an account?{' '}
                  <Link to="/login" className="text-red-500 font-medium hover:underline">
                    Sign in
                  </Link>
                </p>
              </motion.div>
            )}

            {/* Step 2: OTP Verification */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <button onClick={() => setStep(1)} className="flex items-center gap-1 text-slate-500 hover:text-slate-700 mb-4">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>

                <h1 className="text-2xl font-bold text-slate-900 mb-2">Verify Your Email</h1>
                <p className="text-slate-600 mb-6">
                  We sent a 6-digit code to <strong>{email}</strong>
                </p>

                <div className="flex gap-2 justify-center mb-6">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={otpRefs[index]}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      className="w-12 h-14 text-center text-2xl font-bold border-2 border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                  ))}
                </div>

                <Button
                  onClick={handleVerifyOTP}
                  disabled={otpVerifying || otp.join('').length !== 6}
                  className="w-full bg-red-500 hover:bg-red-600 py-6"
                >
                  {otpVerifying ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Check className="w-5 h-5 mr-2" />
                  )}
                  Verify Code
                </Button>

                <p className="text-center text-sm text-slate-500 mt-4">
                  {resendTimer > 0 ? (
                    <>Resend code in {resendTimer}s</>
                  ) : (
                    <button onClick={handleSendOTP} disabled={otpSending} className="text-red-500 font-medium hover:underline">
                      Resend Code
                    </button>
                  )}
                </p>
              </motion.div>
            )}

            {/* Step 3: Password */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <button onClick={() => setStep(2)} className="flex items-center gap-1 text-slate-500 hover:text-slate-700 mb-4">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>

                <h1 className="text-2xl font-bold text-slate-900 mb-2">Create Password</h1>
                <p className="text-slate-600 mb-6">Secure your account with a strong password</p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Min. 8 characters"
                        className="w-full pl-10 pr-12 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Confirm Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Confirm your password"
                        className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      />
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleRegister}
                  disabled={registering}
                  className="w-full mt-6 bg-red-500 hover:bg-red-600 py-6"
                >
                  {registering ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Shield className="w-5 h-5 mr-2" />
                  )}
                  Create Account
                </Button>
              </motion.div>
            )}

            {/* Step 4: Payment Selection */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-8 h-8 text-green-600" />
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-2">Account Created!</h1>
                  <p className="text-slate-600">Now complete your purchase to access all features</p>
                </div>

                {/* Selected Package Display */}
                {selectedPackage && (
                  <div className="bg-gradient-to-r from-red-50 to-amber-50 rounded-2xl p-6 border border-red-100 mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <Badge className="bg-red-100 text-red-700 mb-2">Selected Package</Badge>
                        <h3 className="text-xl font-bold text-slate-900">{selectedPackage.name}</h3>
                      </div>
                      <div className="text-right">
                        <span className="text-3xl font-bold text-slate-900">£{selectedPackage.price}</span>
                        <p className="text-sm text-slate-500">one-time</p>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600">{selectedPackage.description}</p>
                  </div>
                )}

                {/* Payment Methods */}
                <div className="space-y-3">
                  <p className="text-sm font-medium text-slate-700">Choose Payment Method:</p>
                  
                  {/* Show PayPal buttons inline when selected */}
                  {showPayPalButtons ? (
                    <div className="border-2 border-yellow-300 rounded-xl p-4 bg-yellow-50">
                      <button 
                        onClick={() => setShowPayPalButtons(false)} 
                        className="flex items-center gap-1 text-slate-500 hover:text-slate-700 mb-4 text-sm"
                      >
                        <ArrowLeft className="w-4 h-4" /> Back to payment methods
                      </button>
                      
                      {processingPayment ? (
                        <div className="flex flex-col items-center py-8">
                          <Loader2 className="w-8 h-8 animate-spin text-yellow-500 mb-4" />
                          <p className="text-slate-600">Processing your payment...</p>
                        </div>
                      ) : (
                        <PayPalScriptProvider options={{ 
                          clientId: PAYPAL_CLIENT_ID, 
                          currency: 'GBP',
                          intent: 'capture'
                        }}>
                          <PayPalButtons
                            style={{ layout: 'vertical', color: 'blue', shape: 'rect', label: 'pay' }}
                            createOrder={createPayPalOrder}
                            onApprove={onPayPalApprove}
                            onError={onPayPalError}
                            onCancel={onPayPalCancel}
                          />
                        </PayPalScriptProvider>
                      )}
                    </div>
                  ) : paymentSuccess ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">Payment Submitted!</h3>
                      <p className="text-slate-600 mb-4">Your account will be activated after payment verification.</p>
                      <Button onClick={handleSkipPayment} className="bg-green-600 hover:bg-green-700">
                        Go to Dashboard
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Stripe - Credit/Debit Card */}
                      <PaymentMethodButton
                        icon={<CreditCard className="w-6 h-6 text-blue-600" />}
                        bgColor="bg-blue-100"
                        hoverColor="hover:border-blue-300 hover:bg-blue-50"
                        title="Credit / Debit Card"
                        subtitle="Pay securely with Stripe"
                        onClick={() => handleStripePayment()}
                      />

                      {/* PayPal */}
                      <PaymentMethodButton
                        icon={<PayPalIcon />}
                        bgColor="bg-yellow-100"
                        hoverColor="hover:border-yellow-300 hover:bg-yellow-50"
                        title="PayPal"
                        subtitle="Pay with your PayPal account"
                        onClick={() => handlePayPalPayment()}
                      />

                      {/* Pakistan-specific options */}
                      {isPakistan && (
                        <>
                          {/* Easypaisa Accordion */}
                          <EasypaisaAccordion 
                            selectedPackage={selectedPackage}
                            registeredUser={registeredUser}
                            onSuccess={() => setPaymentSuccess(true)}
                          />

                          {/* Bank Transfer Accordion */}
                          <BankTransferAccordion 
                            selectedPackage={selectedPackage}
                            registeredUser={registeredUser}
                            onSuccess={() => setPaymentSuccess(true)}
                          />
                        </>
                      )}
                    </>
                  )}
                </div>

                <button
                  onClick={handleSkipPayment}
                  className="w-full mt-4 text-center text-sm text-slate-500 hover:text-slate-700"
                >
                  Continue to Dashboard (Buy Later) →
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Right Side - Package Info with Large Image */}
      <div className="hidden lg:flex w-[550px] bg-gradient-to-br from-slate-900 via-slate-800 to-red-900/30 flex-col justify-center relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-10 w-64 h-64 bg-red-500 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-48 h-48 bg-amber-500 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 p-10 text-white">
          {/* Large Founder Image */}
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <div className="w-56 h-56 rounded-3xl overflow-hidden border-4 border-white/20 shadow-2xl">
                <img 
                  src={AHMAD_RAZA_IMAGE}
                  alt="Ahmad Raza - Visa Expert"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-3 -right-3 bg-green-500 text-white px-4 py-2 rounded-xl shadow-lg">
                <p className="text-xs font-bold">Proven Track</p>
                <p className="text-xs">Record</p>
              </div>
            </div>
          </div>
          
          {/* Founder Info */}
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold">Ahmad Raza</h3>
            <p className="text-red-400">Founder & UK Visa Expert</p>
          </div>

          <h2 className="text-2xl font-bold mb-4 text-center">
            Start Your UK Visa Journey
          </h2>
          <p className="text-slate-400 mb-6 text-center text-sm">
            Join thousands of entrepreneurs who have successfully obtained their Innovator Founder Visa.
          </p>

          <div className="space-y-3">
            <div className="flex items-center gap-3 bg-white/5 rounded-xl p-3">
              <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Check className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="font-medium text-sm">AI-Powered Business Plan Builder</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/5 rounded-xl p-3">
              <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Check className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="font-medium text-sm">Mock Interview Preparation</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/5 rounded-xl p-3">
              <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Check className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="font-medium text-sm">Expert Guidance & Support</p>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-white/5 rounded-xl border border-white/10">
            <p className="text-sm text-slate-300 italic text-center">
              &quot;I&apos;ve helped over 200+ entrepreneurs get their UK visa approved. This platform combines everything I&apos;ve learned into an easy-to-use system.&quot;
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
